/**
 * Standard authenticated app route: registry access check + optional auth layers.
 */

import { ReactNode } from 'react'
import { AccessRoute } from '@/components/routing/AccessRoute'
import { KnownRoute } from '@/components/routing/KnownRoute'
import { ProtectedRoute } from '@/components/routing/ProtectedRoute'
import { getRouteById } from '@/lib/routing/route-registry'
import type { RouteId } from '@/lib/routing/types'

/** How to gate auth after registry access passes. */
export type RouteAuthMode = 'none' | 'session' | 'known'

interface AppRouteProps {
  routeId: RouteId
  children: ReactNode
  skipOnboardingCheck?: boolean
  autoSwitchOrgContext?: boolean
  /** Override auth layer; defaults from route registry (`requiresKnownUser` → known). */
  authMode?: RouteAuthMode
}

function resolveAuthMode(routeId: RouteId, authMode?: RouteAuthMode): RouteAuthMode {
  if (authMode) return authMode
  const route = getRouteById(routeId)
  if (route?.requiresKnownUser) return 'known'
  return 'none'
}

function wrapWithAuth(children: ReactNode, mode: RouteAuthMode, skipOnboardingCheck: boolean) {
  switch (mode) {
    case 'known':
      return <KnownRoute skipOnboardingCheck={skipOnboardingCheck}>{children}</KnownRoute>
    case 'session':
      return <ProtectedRoute skipOnboardingCheck={skipOnboardingCheck}>{children}</ProtectedRoute>
    default:
      return children
  }
}

export function AppRoute({
  routeId,
  children,
  skipOnboardingCheck = false,
  autoSwitchOrgContext = false,
  authMode,
}: AppRouteProps) {
  const resolvedAuthMode = resolveAuthMode(routeId, authMode)

  return (
    <AccessRoute routeId={routeId} autoSwitchOrgContext={autoSwitchOrgContext}>
      {wrapWithAuth(children, resolvedAuthMode, skipOnboardingCheck)}
    </AccessRoute>
  )
}
